import { useState, useRef, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

interface UseVoiceSearchProps {
  onTranscriptionComplete: (text: string) => void;
  onError?: (error: string) => void;
  maxDuration?: number;
}

interface UseVoiceSearchReturn {
  isRecording: boolean;
  isTranscribing: boolean;
  startRecording: () => Promise<void>;
  stopRecording: () => void;
  error: string | null;
}

export function useVoiceSearch({
  onTranscriptionComplete,
  onError,
  maxDuration = 10000,
}: UseVoiceSearchProps): UseVoiceSearchReturn {
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const timeoutRef = useRef<number | null>(null);

  const cleanup = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    mediaRecorderRef.current = null;
    chunksRef.current = [];
  }, []);

  const transcribeAudio = useCallback(async (audioBlob: Blob) => {
    setIsTranscribing(true);
    setError(null);

    try {
      const reader = new FileReader();
      reader.readAsDataURL(audioBlob);

      await new Promise((resolve, reject) => {
        reader.onloadend = resolve;
        reader.onerror = reject;
      });

      const base64Audio = (reader.result as string).split(',')[1];

      const { data, error: invokeError } = await supabase.functions.invoke('transcribe-voice-search', {
        body: {
          audioBase64: base64Audio,
          mimeType: audioBlob.type
        }
      });

      if (invokeError) {
        throw new Error(invokeError.message);
      }

      if (data?.text) {
        onTranscriptionComplete(data.text);
      } else {
        throw new Error('Transcrição vazia recebida');
      }

    } catch (err: any) {
      const errorMessage = err.message || 'Erro ao transcrever áudio';
      setError(errorMessage);
      onError?.(errorMessage);
    } finally {
      setIsTranscribing(false);
    }
  }, [onTranscriptionComplete, onError]);

  const stopRecording = useCallback(() => {
    if (!mediaRecorderRef.current || !isRecording) return;

    mediaRecorderRef.current.stop();
    setIsRecording(false);

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, [isRecording]);

  const startRecording = useCallback(async () => {
    setError(null);
    chunksRef.current = [];

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          sampleRate: 16000,
          echoCancellation: true,
          noiseSuppression: true,
        }
      });

      streamRef.current = stream;

      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : 'audio/webm';

      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(chunksRef.current, { type: mimeType });
        cleanup();
        transcribeAudio(audioBlob);
      };

      mediaRecorder.start();
      setIsRecording(true);

      timeoutRef.current = window.setTimeout(() => {
        stopRecording();
      }, maxDuration);

    } catch (err: any) {
      let errorMessage = 'Erro ao acessar microfone';

      if (err.name === 'NotAllowedError') {
        errorMessage = 'Permissão de microfone negada. Habilite nas configurações do navegador.';
      } else if (err.name === 'NotFoundError') {
        errorMessage = 'Nenhum microfone encontrado.';
      }

      setError(errorMessage);
      onError?.(errorMessage);
      cleanup();
    }
  }, [maxDuration, cleanup, transcribeAudio, stopRecording, onError]);

  return {
    isRecording,
    isTranscribing,
    startRecording,
    stopRecording,
    error,
  };
}
